import { Fragment, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import type { SiteInfo } from '@/graph/modules/sites';
import { getGuestUsers, getSiteFileGrants, type ExternalGrant } from '@/graph/modules/guestAccess';
import { aggregateGuestAccess, type GuestAccessResult } from './guestAccess';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { SitePicker } from '@/components/SitePicker';
import { Metric, MetricGrid } from '@/components/Metric';
import { ProgressBar } from '@/components/ProgressBar';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconShare } from '@/components/icons';
import { readToken } from '@/lib/theme';

type Status = 'idle' | 'scanning' | 'done' | 'error';

/**
 * C13 — External / guest file access. Lists which external users can reach which
 * SharePoint / OneDrive files: a site-level overview plus a bounded per-file
 * drill-down (expand a guest to see the files). Read-only, best-effort.
 */
export function GuestAccessPanel() {
  const [status, setStatus] = useState<Status>('idle');
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [maxFiles, setMaxFiles] = useState('50');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState<GuestAccessResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set<string>());
  const cancelRef = useRef(false);

  const accent = readToken('--accent', '#5C7E8F');
  const red = readToken('--red', '#9E4F4F');
  const grid = readToken('--border', '#D4DDE2');
  const text3 = readToken('--text-3', '#6E7478');

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function run() {
    cancelRef.current = false;
    setStatus('scanning');
    setResult(null);
    setError(null);
    setDone(0);
    setTotal(0);
    const fileLimit = Math.max(1, Math.min(1000, Number(maxFiles) || 50));
    try {
      const guests = await getGuestUsers();
      const guestIds = new Set(guests.map((g) => g.id));
      const guestEmails = new Set(
        guests.map((g) => g.mail?.toLowerCase()).filter((x): x is string => !!x),
      );
      setTotal(sites.length);
      const grants: ExternalGrant[] = [];
      let i = 0;
      for (const site of sites) {
        if (cancelRef.current) break;
        grants.push(...(await getSiteFileGrants(site, guestIds, guestEmails, fileLimit)));
        setDone(++i);
      }
      setResult(aggregateGuestAccess(guests.length, sites.length, grants));
      setStatus('done');
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }

  const guestChart = result
    ? result.byGuest.slice(0, 10).map((g) => ({
        name: g.name.length > 22 ? g.name.slice(0, 21) + '…' : g.name,
        files: g.fileCount,
      }))
    : [];
  const siteChart = result
    ? result.bySite.slice(0, 10).map((s) => ({
        name: s.site.length > 22 ? s.site.slice(0, 21) + '…' : s.site,
        files: s.files,
      }))
    : [];

  return (
    <Panel id="guests" icon={<IconShare />} title="External / guest file access" cap="C13"
      why={CAPABILITY_SCOPES.C13.why}>
      <ModuleGate cap="C13">
        <h3 className="metric__label">Choose which sites to scan</h3>
        <SitePicker selected={sites} onChange={setSites} disabled={status === 'scanning'} />

        <div className="field" style={{ maxWidth: 180 }}>
          <label htmlFor="ga-files">Max files / site</label>
          <input id="ga-files" inputMode="numeric" value={maxFiles} disabled={status === 'scanning'}
            onChange={(e) => setMaxFiles(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>

        <div className="gate__cta" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn--primary" onClick={() => void run()}
            disabled={status === 'scanning' || sites.length === 0}>
            {status === 'scanning'
              ? 'Scanning…'
              : result
                ? 'Re-scan'
                : `Scan ${sites.length} selected site${sites.length === 1 ? '' : 's'}`}
          </button>
          {status === 'scanning' && <button className="btn btn--ghost" onClick={() => { cancelRef.current = true; }}>Stop</button>}
        </div>
        {sites.length === 0 && status !== 'scanning' && (
          <p className="small muted" style={{ marginTop: 8 }}>Search and select at least one site to scan.</p>
        )}

        {status === 'scanning' && (
          <div style={{ marginTop: 16 }}>
            <ProgressBar value={done} max={total} label="Scanning site permissions" />
          </div>
        )}

        {status === 'error' && <ErrorNotice error={error} />}

        {result && (
          <>
            <MetricGrid>
              <Metric label="Guest users (tenant)" value={result.totalGuests} sub="userType = Guest" />
              <Metric label="With file access" value={result.guestsWithFileAccess}
                tone={result.guestsWithFileAccess > 0 ? 'red' : 'green'} sub="External users on files" />
              <Metric label="Files exposed" value={result.filesExposed}
                tone={result.filesExposed > 0 ? 'red' : 'green'} sub="Files with an external grant" />
              <Metric label="Sites scanned" value={result.sitesScanned} />
            </MetricGrid>

            {result.byGuest.length === 0 ? (
              <div className="notice notice--info">
                <strong>No external file access found</strong>
                <p>None of the scanned files carry a permission granted to an external/guest identity. Widen the
                  scan (more sites/files) to check further — the scan is bounded and best-effort.</p>
              </div>
            ) : (
              <>
                <div className="chart-grid">
                  <div>
                    <h3 className="metric__label">Top external users by files</h3>
                    <div className="chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={guestChart} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: text3 }}
                            tickLine={false} axisLine={{ stroke: grid }} />
                          <YAxis type="category" dataKey="name" width={150}
                            tick={{ fontSize: 11, fill: text3 }} tickLine={false} axisLine={false} />
                          <Tooltip cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                            contentStyle={{ borderRadius: 10, border: `1px solid ${grid}`, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                          <Bar dataKey="files" name="Files" fill={red} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <h3 className="metric__label">Most-exposed sites</h3>
                    <div className="chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={siteChart} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: text3 }}
                            tickLine={false} axisLine={{ stroke: grid }} />
                          <YAxis type="category" dataKey="name" width={150}
                            tick={{ fontSize: 11, fill: text3 }} tickLine={false} axisLine={false} />
                          <Tooltip cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                            contentStyle={{ borderRadius: 10, border: `1px solid ${grid}`, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                          <Bar dataKey="files" name="Files" fill={accent} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Site-level overview */}
                <h3 className="metric__label" style={{ marginTop: 8 }}>Site overview</h3>
                <table className="matrix">
                  <thead>
                    <tr><th>Site</th><th>External users</th><th>Files exposed</th></tr>
                  </thead>
                  <tbody>
                    {result.bySite.slice(0, 100).map((s) => (
                      <tr key={s.site}>
                        <td className="matrix__label">{s.site}</td>
                        <td>{s.guests}</td>
                        <td><span className="chip chip--warn">{s.files}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Guest-level drill-down */}
                <h3 className="metric__label" style={{ marginTop: 14 }}>
                  External users — {result.byGuest.length} <span className="small muted">(click to see files)</span>
                </h3>
                <table className="matrix matrix--expandable">
                  <thead>
                    <tr><th style={{ width: 24 }}></th><th>External user</th><th>Sites</th><th>Files</th><th>Roles</th></tr>
                  </thead>
                  <tbody>
                    {result.byGuest.slice(0, 200).map((g) => {
                      const key = g.id ?? g.email ?? g.name;
                      const isOpen = expanded.has(key);
                      return (
                        <Fragment key={key}>
                          <tr className="matrix__row--toggle" onClick={() => toggle(key)} aria-expanded={isOpen}>
                            <td className="matrix__caret">{isOpen ? '▾' : '▸'}</td>
                            <td className="matrix__label">
                              {g.name}
                              {g.email && g.email !== g.name && (
                                <span className="small muted" style={{ display: 'block' }}>{g.email}</span>
                              )}
                            </td>
                            <td>{g.siteCount}</td>
                            <td><span className="chip chip--critical">{g.fileCount}</span></td>
                            <td className="matrix__detail"><code className="small">{g.roles.join(', ')}</code></td>
                          </tr>
                          {isOpen && (
                            <tr className="matrix__detailrow">
                              <td></td>
                              <td colSpan={4}>
                                <div style={{ paddingTop: 10 }}>
                                  <span className="evrow__h">Files accessible ({g.files.length})</span>
                                  <table className="matrix" style={{ marginTop: 4 }}>
                                    <thead>
                                      <tr><th>Site</th><th>File</th><th>Role</th><th>Via</th></tr>
                                    </thead>
                                    <tbody>
                                      {g.files.slice(0, 200).map((f, i) => (
                                        <tr key={i}>
                                          <td className="matrix__detail">{f.site}</td>
                                          <td className="matrix__label">{f.file}</td>
                                          <td className="matrix__detail"><code className="small">{f.role}</code></td>
                                          <td className="matrix__detail">{f.kind}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            <p className="small muted" style={{ marginTop: 12 }}>
              Bounded, best-effort scan of each site's default document library. "Via" shows how access was granted
              (direct permission, sharing link, or pending invitation). Widen max sites/files for deeper coverage.
              Generated client-side; nothing leaves your browser.
            </p>
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
